import React, { useState } from "react";
import Snackbar from "../UI/Snackbar";
import { getApiUrl } from "../../config/api";
import { useSnackbar } from "../../shared/hooks/useSnackbar";

interface FilePreviewProps {
  file: File | null;
  onRemove: () => void;
  label: string;
}

const FilePreview: React.FC<FilePreviewProps> = ({ file, onRemove, label }) => {
  if (!file) return null;

  const fileUrl = URL.createObjectURL(file);

  return (
    <div className="mt-3 p-4 bg-gray-50 rounded-lg border">
      <div className="flex items-start space-x-3">
        <img
          src={fileUrl}
          alt={`${label} preview`}
          className="w-16 h-16 object-cover rounded-lg border"
          onLoad={() => URL.revokeObjectURL(fileUrl)}
        />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-800 truncate">
            {file.name}
          </p>
          <p className="text-xs text-gray-500 mt-1">
            {(file.size / 1024).toFixed(1)} KB
          </p>
        </div>
        <button
          type="button"
          onClick={onRemove}
          className="flex-shrink-0 bg-red-100 hover:bg-red-200 text-red-600 
                     px-3 py-1 rounded-lg text-sm font-medium transition-colors 
                     duration-200 focus:outline-none focus:ring-2 focus:ring-red-500"
        >
          Remove
        </button>
      </div>
    </div>
  );
};

const CreateCategory: React.FC = () => {
  const [formData, setFormData] = useState({
    categoryName: "",
    slug: "",
    iconFile: null as File | null,
    webImageFile: null as File | null,
  });

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const {
    showSnackbar,
    snackbarType,
    apiMessage,
    setShowSnackbar,
    showMessage,
  } = useSnackbar();

  const handleInputChange = (field: string, value: string | File | null) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleFileRemove = (field: "iconFile" | "webImageFile") => {
    setFormData((prev) => ({
      ...prev,
      [field]: null,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.categoryName.trim() || !formData.slug.trim()) {
      setError("Category Name and Slug are required.");
      showMessage("Category Name and Slug are required.", "error");
      return;
    }

    if (!formData.iconFile || !formData.webImageFile) {
      setError("Both PNG files are required.");
      showMessage("Both PNG files are required.", "error");
      return;
    }

    setIsLoading(true);

    try {
      const apiFormData = new FormData();
      apiFormData.append("name", formData.categoryName);
      apiFormData.append("slug", formData.slug);
      apiFormData.append("iconImage", formData.iconFile);
      apiFormData.append("webImage", formData.webImageFile);

      const response = await fetch(
        getApiUrl("/api/templates/v1.0.0/templates-category"),
        {
          method: "POST",
          headers: {
            accept: "application/json",
          },
          body: apiFormData,
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message || `HTTP error! status: ${response.status}`
        );
      }

      const result = await response.json();
      console.log("Category created successfully:", result);

      // Reset form
      setFormData({
        categoryName: "",
        slug: "",
        iconFile: null,
        webImageFile: null,
      });

      showMessage("Category created successfully!", "success");
    } catch (err) {
      console.error("Error creating category:", err);

      setError(
        err instanceof Error
          ? err.message
          : "Failed to create category. Please try again."
      );
      showMessage(
        err instanceof Error
          ? err.message
          : "Failed to create category. Please try again.",
        "error"
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto my-10">
      <div className="bg-white p-8 rounded-2xl shadow-lg border border-gray-100">
        <div className="mb-6">
          <h3 className="text-2xl font-bold text-gray-800 mb-2">
            Create Category
          </h3>
          <p className="text-gray-500 text-sm">
            Fill out the details below to add a new category.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Category Name */}
          <div>
            <label htmlFor="categoryName" className="block text-sm font-semibold text-gray-700 mb-2">
              Category Name *
            </label>
            <input
              id="categoryName"
              type="text"
              value={formData.categoryName}
              onChange={(e) =>
                handleInputChange("categoryName", e.target.value)
              }
              className="w-full px-4 py-3 border border-gray-300 rounded-xl 
                         focus:ring-2 focus:ring-blue-500 focus:border-transparent 
                         outline-none transition-all duration-200 hover:border-gray-400"
              placeholder="Enter category name"
              required
            />
          </div>

          {/* Slug */}
          <div>
            <label htmlFor="slug" className="block text-sm font-semibold text-gray-700 mb-2">
              Slug Value *
            </label>
            <input
              id="slug"
              type="text"
              value={formData.slug}
              onChange={(e) => handleInputChange("slug", e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl 
                         focus:ring-2 focus:ring-blue-500 focus:border-transparent 
                         outline-none transition-all duration-200 hover:border-gray-400"
              placeholder="Enter slug value"
              required
            />
          </div>

          {/* PNG Icon File */}
          <div>
            <label htmlFor="iconFile" className="block text-sm font-semibold text-gray-700 mb-2">
              PNG Icon Image File *
            </label>
            {formData.iconFile ? (
              <FilePreview
                file={formData.iconFile}
                onRemove={() => handleFileRemove("iconFile")}
                label="Icon"
              />
            ) : (
              <input
                id="iconFile"
                type="file"
                accept="image/png"
                onChange={(e) =>
                  handleInputChange("iconFile", e.target.files?.[0] || null)
                }
                className="w-full px-4 py-3 border border-gray-300 rounded-xl cursor-pointer
                           focus:ring-2 focus:ring-blue-500 focus:border-transparent 
                           outline-none transition-all duration-200 hover:border-gray-400"
                required
              />
            )}
          </div>

          {/* PNG Web Image File */}
          <div>
            <label htmlFor="webImageFile" className="block text-sm font-semibold text-gray-700 mb-2">
              PNG Web Image File *
            </label>
            {formData.webImageFile ? (
              <FilePreview
                file={formData.webImageFile}
                onRemove={() => handleFileRemove("webImageFile")}
                label="Web Image"
              />
            ) : (
              <input
                id="webImageFile"
                type="file"
                accept="image/png"
                onChange={(e) =>
                  handleInputChange("webImageFile", e.target.files?.[0] || null)
                }
                className="w-full px-4 py-3 border border-gray-300 rounded-xl cursor-pointer
                           focus:ring-2 focus:ring-blue-500 focus:border-transparent 
                           outline-none transition-all duration-200 hover:border-gray-400"
                required
              />
            )}
          </div>

          {/* Submit */}
          <button
            type="submit"
            disabled={isLoading}
            className={`w-full py-4 px-6 rounded-xl font-semibold text-lg 
                       transition-all duration-200 flex items-center justify-center 
                       space-x-2 shadow-lg focus:outline-none focus:ring-2 focus:ring-offset-2
                       ${
                         isLoading
                           ? "bg-gray-400 cursor-not-allowed"
                           : "bg-gradient-to-r from-green-500 to-teal-500 text-white hover:from-green-600 hover:to-teal-600 hover:shadow-xl transform hover:scale-105 focus:ring-green-500"
                       }`}
          >
            {isLoading ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                <span>Creating Category...</span>
              </>
            ) : (
              <span>Add Category</span>
            )}
          </button>
        </form>
      </div>

      {/* Snackbar */}
      <Snackbar
        isOpen={showSnackbar}
        onClose={() => {
          setShowSnackbar(false);
          setError(null);
        }}
        type={snackbarType}
        title={snackbarType === "error" ? "Error" : "Success"}
        message={error || apiMessage || ""}
        apiMessage={apiMessage || undefined}
        autoClose={snackbarType === "success"}
        autoCloseDelay={5000}
      />
    </div>
  );
};

export default CreateCategory;
